<script setup>
import { computed, ref } from 'vue';
import GuaranteeItem from './GuaranteeItem.vue';

const props = defineProps({
  rows: { type: Array, default: () => [] },
  startExpanded: { type: Boolean, default: false },
});

const expanded = ref(props.startExpanded);
const activeDialog = ref(null);

const formatBRL = value =>
  value == null
    ? '—'
    : new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
      }).format(value);

const regularRows = computed(() =>
  props.rows.filter(row => row.kind !== 'TOTAL_COST')
);

const totalRow = computed(
  () => props.rows.find(row => row.kind === 'TOTAL_COST') || null
);

const displayValue = row => row.valueLabel || formatBRL(row.value);
const displayOldValue = row =>
  row.oldValueLabel || (row.oldValue == null ? '' : formatBRL(row.oldValue));
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <div class="rounded-lais border border-lais-border bg-white">
    <button
      type="button"
      class="flex w-full items-center justify-between gap-3 px-4 py-4 text-left"
      @click="expanded = !expanded"
    >
      <div>
        <p
          class="text-[12px] font-semibold uppercase tracking-wide text-lais-text-muted"
        >
          Tabela de preço
        </p>
        <p
          v-if="totalRow"
          class="mt-1 font-redHatDisplay text-[26px] font-semibold leading-none text-lais-primary"
        >
          {{ formatBRL(totalRow.value) }}
        </p>
      </div>
      <span
        class="i-lucide-chevron-down size-5 text-lais-text-muted transition-transform"
        :class="expanded ? 'rotate-180' : ''"
        aria-hidden="true"
      />
    </button>

    <div v-if="expanded" class="border-t border-lais-border px-4 py-3">
      <div class="flex flex-col gap-3">
        <div v-for="row in regularRows" :key="row.kind" class="text-[13px]">
          <GuaranteeItem
            v-if="row.kind === 'RENTAL_GUARANTEE'"
            :row="row"
            @open="activeDialog = $event"
          />
          <div v-else class="flex items-start justify-between gap-3">
            <div class="min-w-0">
              <div class="flex items-center gap-1.5">
                <p class="font-semibold text-lais-text">{{ row.label }}</p>
                <button
                  v-if="row.dialog"
                  type="button"
                  class="grid size-6 place-items-center rounded-full text-lais-text-muted transition-colors hover:bg-lais-bg-soft hover:text-lais-primary"
                  :aria-label="`Ver detalhes de ${row.label}`"
                  @click="activeDialog = row.dialog"
                >
                  <span class="i-lucide-info size-3.5" aria-hidden="true" />
                </button>
              </div>
              <p
                v-if="row.percentage"
                class="mt-0.5 text-[12px] text-lais-text-muted"
              >
                {{ row.percentage }}% aplicado nesta conta
              </p>
            </div>
            <div class="shrink-0 text-right">
              <p
                v-if="row.oldValue != null || row.oldValueLabel"
                class="text-[12px] font-medium text-lais-text-muted line-through"
              >
                {{ displayOldValue(row) }}
              </p>
              <p class="font-semibold text-lais-text">
                {{ displayValue(row) }}
              </p>
            </div>
          </div>
        </div>

        <div
          v-if="totalRow"
          class="flex items-start justify-between gap-3 border-t border-dashed border-lais-border pt-3"
        >
          <div class="flex items-center gap-1.5">
            <p class="font-semibold text-lais-text">{{ totalRow.label }}</p>
            <button
              v-if="totalRow.dialog"
              type="button"
              class="grid size-6 place-items-center rounded-full text-lais-text-muted transition-colors hover:bg-lais-bg-soft hover:text-lais-primary"
              :aria-label="`Ver detalhes de ${totalRow.label}`"
              @click="activeDialog = totalRow.dialog"
            >
              <span class="i-lucide-info size-3.5" aria-hidden="true" />
            </button>
          </div>
          <div class="text-right">
            <p
              v-if="totalRow.oldValue != null || totalRow.oldValueLabel"
              class="text-[12px] font-medium text-lais-text-muted line-through"
            >
              {{ displayOldValue(totalRow) }}
            </p>
            <p class="font-semibold text-lais-primary">
              {{ formatBRL(totalRow.value) }}
            </p>
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="activeDialog"
      class="fixed inset-0 z-50 grid place-items-center bg-black/40 px-4"
      role="dialog"
      aria-modal="true"
      @click.self="activeDialog = null"
    >
      <div
        class="w-full max-w-[460px] rounded-lais border border-lais-border bg-white p-5 shadow-lais-card"
      >
        <div class="flex items-start justify-between gap-4">
          <div>
            <h3 class="text-[17px] font-semibold text-lais-text">
              {{ activeDialog.title }}
            </h3>
            <p
              v-if="activeDialog.description"
              class="mt-2 text-[13px] leading-relaxed text-lais-text-body"
            >
              {{ activeDialog.description }}
            </p>
          </div>
          <button
            type="button"
            class="grid size-8 shrink-0 place-items-center rounded-full text-lais-text-muted transition-colors hover:bg-lais-bg-soft hover:text-lais-primary"
            aria-label="Fechar"
            @click="activeDialog = null"
          >
            <span class="i-lucide-x size-4" aria-hidden="true" />
          </button>
        </div>
        <ul
          v-if="activeDialog.items?.length"
          class="mt-4 grid gap-2 text-[13px] text-lais-text-body"
        >
          <li v-for="item in activeDialog.items" :key="item" class="flex gap-2">
            <span
              class="i-lucide-check mt-0.5 size-4 shrink-0 text-lais-primary"
              aria-hidden="true"
            />
            <span>{{ item }}</span>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>
